var _sentryCollisionFreeGlobalObject = typeof window === "undefined" ? global : window;
_sentryCollisionFreeGlobalObject["__sentryRewritesTunnelPath__"] = undefined;
_sentryCollisionFreeGlobalObject["SENTRY_RELEASE"] = {"id":"1"};
_sentryCollisionFreeGlobalObject["__rewriteFramesAssetPrefixPath__"] = "";

import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum-slim';
import * as Sentry from '@sentry/nextjs';
import type { ClientOptions, ErrorEvent, EventHint } from '@sentry/types';
import { z } from 'zod';
import { IS_LOCAL_HOST } from '@utils/constants';
import { FormValidationError } from '@utils/error';
import { beforeBreadcrumb } from '@utils/sentry-helpers';

const SENTRY_DSN = 'https://272e2eb2bc864e23bcdc8ececcf0d208@o997830.ingest.sentry.io/5956174';
const DATADOG_SAMPLING = 100;

/**
 * Filters known (ignorable) errors out before sending them to Sentry. Also, adds tags to the event.
 * Intended as a {@link ClientOptions.beforeSend} callback. Returning null filters the error from Sentry.
 */
export const beforeSend: Required<ClientOptions>['beforeSend'] = (
  event: ErrorEvent,
  hint: EventHint,
) => {
  if (shouldRejectError(hint.originalException)) {
    // https://docs.sentry.io/platforms/javascript/configuration/filtering/#filtering-error-events
    // null drops event
    return null;
  }

  return event;
};

/** Identifies ethers request errors */
function isEthersRequestError(error: Error): error is Error & { requestBody: string } {
  return (
    'requestBody' in error &&
    typeof (error as unknown as Record<'requestBody', unknown>).requestBody === 'string'
  );
}

const EthersRequestBodySchema = z.object({
  method: z.string(),
});

// src: https://github.com/Uniswap/interface/blob/main/src/tracing/errors.ts
function shouldRejectError(error: EventHint['originalException']) {
  if (error instanceof Error) {
    // ethers aggressively polls for block number, and it sometimes fails (whether spuriously or through rate-limiting).
    // If block number polling, it should not be considered an exception.
    if (isEthersRequestError(error)) {
      const parsed = EthersRequestBodySchema.safeParse(JSON.parse(error.requestBody));
      if (parsed.success && parsed.data.method === 'eth_blockNumber') return true;
    }

    // If the error is a network change, it should not be considered an exception.
    if (error.message.match(/underlying network changed/)) return true;

    // This is caused by HTML being returned for a chunk from Cloudflare.
    // Usually, it's the result of a 499 exception right before it, which should be handled.
    // Therefore, this can be ignored.
    if (error.message.match(/Unexpected token '<'/)) return true;

    // Errors coming from a browser extension can be ignored. These errors are usually caused by extensions injecting
    // scripts into the page, which we cannot control.
    if (error.stack?.match(/-extension:\/\//i)) return true;

    // Errors coming from OneKey (a desktop wallet) can be ignored for now.
    // These errors are either application-specific, or they will be thrown separately outside of OneKey.
    if (error.stack?.match(/OneKey/i)) return true;

    // Content security policy 'unsafe-eval' errors can be filtered out because there are expected failures.
    // For example, if a user runs an eval statement in console this error would still get thrown.
    if (error.message.match(/'unsafe-eval'.*content security policy/i)) {
      return true;
    }

    // WebAssembly compilation fails because we do not allow 'unsafe-eval' in our CSP.
    // Any thrown errors are due to 3P extensions/applications, so we do not need to handle them.
    if (
      error.message.match(
        /WebAssembly.instantiate\(\): Wasm code generation disallowed by embedder/,
      )
    ) {
      return true;
    }

    // Filters out errors caused by checking for meta tags that may not exist.
    if (
      error.message.match(
        /null is not an object \(evaluating 'document\.querySelector\('meta\[[^\]]+\]'\)\.content'\)/,
      )
    ) {
      return true;
    }

    // These are caused by user navigation away from the page before a request has finished.
    if (error instanceof DOMException && error.name === 'AbortError') return true;
    // Trying to manipulate the DOM during a state where it is not allowed, such as when the document is still being loaded.
    if (error instanceof DOMException && error.name === 'OperationError') return true;
    // These are custom errors thrown by our code, which we have UX to handle. We don't need to send these to Sentry.
    if (error instanceof FormValidationError) return true;
  }

  return false;
}

Sentry.init({
  dsn: SENTRY_DSN,
  environment: process.env.NEXT_PUBLIC_DEPLOY_ENVIRONMENT,
  enabled: process.env.NODE_ENV === 'production',
  beforeSend,
  ignoreErrors: [
    'User closed modal',
    'User Rejected',
    'Failed to fetch',
    /^Invariant Violation:(.+)/,
    'AbortError: The user aborted a request.',
    'Load failed',
    /\{"code":-32000,"message":"Failed or Rejected Request"\}/,
    /"code":-32603,"message":"MetaMask is having trouble connecting to the network"/,
    /insufficient funds for intrinsic transaction cost/,
    /\{"code":-32000,"message":"Reject by the user"}/,
    /{"code":-32000,"message":"Request rejected"}/,
    /Network error, fetch failed/,
    /^Unexpected chainId(.+)/, // We have UX that makes sure user connects to the right network
    "Failed to execute 'send' on 'WebSocket': Still in CONNECTING state.", // https://soundxyz.slack.com/archives/C03MAV9GAAK/p1686851067998389
  ],
  beforeBreadcrumb,
});

/**
 * I don't know where else to put this but want it to run for
 * every client-side page load
 */

if (process.env.NEXT_PUBLIC_DEPLOY_ENVIRONMENT !== 'development' && !IS_LOCAL_HOST) {
  // https://datadog-rum-reverse-proxy.soundxyz.workers.dev/

  const env = window.location.href.includes('pre-prod.sound.xyz')
    ? 'pre-prod'
    : process.env.NEXT_PUBLIC_DEPLOY_ENVIRONMENT;

  datadogRum.init({
    applicationId: '0caddd78-ea22-49ec-ab27-3db631238d60',
    clientToken: 'pub3f5e3704d7644892f8275635d585205d',
    site: 'datadoghq.com',
    proxy: 'https://datadogrum.sound.xyz/',
    service: 'webapp',
    env,
    // Specify a version number to identify the deployed version of your application in Datadog
    version: process.env.NEXT_PUBLIC_WEBAPP_VERSION,
    sessionSampleRate: DATADOG_SAMPLING,
    sessionReplaySampleRate: 0,
    trackUserInteractions: true,
    trackFrustrations: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask-user-input',
  });

  datadogLogs.init({
    clientToken: 'pub223d9a7686616d1a30f7590736944870',
    site: 'datadoghq.com',
    service: 'webapp',
    env,
    // Specify a version number to identify the deployed version of your application in Datadog
    version: process.env.NEXT_PUBLIC_WEBAPP_VERSION,
    // Sending all logs instead of sampling
    sessionSampleRate: 100,
    // This can be very noisy so we're turning it off for now
    forwardErrorsToLogs: false,
  });
}
